import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function CustomPaginationActionsTable({
  fields,
  data,
  setDataForm,
  setIsTrueUpdateData,
  setIsTrueDeleteData,
  deleteLabel = "Eliminar",
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  function createData(name, calories, fat) {
    return { name, calories, fat };
  }

  const rows = data.sort((a, b) => (a.calories < b.calories ? -1 : 1));

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const setDataUpdate = (data) => {
    setIsTrueUpdateData(true);
    setDataForm(data);
  };

  const setDataDelete = (data) => {
    setIsTrueDeleteData(true);
    setDataForm(data);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: '8px',
        overflowX: 'auto',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
      }}
    >
      <Table size="small" sx={{ minWidth: 500 }} aria-label="custom pagination table">
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                backgroundColor: '#111827',
                color: '#f9fafb',
                fontWeight: 700,
                fontSize: '0.68rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                borderBottom: '2px solid #dc2626',
                py: 1.5,
                whiteSpace: 'nowrap',
              },
            }}
          >
            {fields.map((row) => (
              <TableCell key={row.name}>{row.label}</TableCell>
            ))}
            <TableCell sx={{ whiteSpace: 'nowrap', minWidth: '140px' }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : rows
          ).map((row, index) => (
            <TableRow
              key={row.id}
              sx={{
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                '&:hover': { backgroundColor: '#fef2f2' },
                transition: 'background-color 0.12s ease',
                '& td': { borderBottom: '1px solid #f1f5f9', py: 1, fontSize: '0.82rem' },
              }}
            >
              {fields.map((item) => (
                <TableCell key={item.name}>{row[item.name]}</TableCell>
              ))}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                  <button
                    onClick={() => setDataUpdate(row)}
                    title="Editar"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      gap: '4px', padding: '4px 10px', borderRadius: '5px',
                      border: '1px solid #d97706', backgroundColor: '#fffbeb',
                      cursor: 'pointer', transition: 'all 0.12s ease', fontSize: '0.7rem',
                      fontWeight: 600, color: '#d97706',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.color = '#fff'; e.currentTarget.querySelector('i').style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fffbeb'; e.currentTarget.style.color = '#d97706'; e.currentTarget.querySelector('i').style.color = '#d97706'; }}
                  >
                    <i className="fa-solid fa-pen-to-square" style={{ fontSize: '11px', color: '#d97706' }}></i>
                    Editar
                  </button>
                  <button
                    onClick={() => setDataDelete(row)}
                    title="Eliminar"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      gap: '4px', padding: '4px 10px', borderRadius: '5px',
                      border: '1px solid #dc2626', backgroundColor: '#fef2f2',
                      cursor: 'pointer', transition: 'all 0.12s ease', fontSize: '0.7rem',
                      fontWeight: 600, color: '#dc2626',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.color = '#fff'; e.currentTarget.querySelector('i').style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.querySelector('i').style.color = '#dc2626'; }}
                  >
                    <i className="fa-solid fa-trash" style={{ fontSize: '11px', color: '#dc2626' }}></i>
                    {deleteLabel}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 45 * emptyRows }}>
              <TableCell colSpan={fields.length + 1} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ '& td': { borderBottom: 'none', backgroundColor: '#f8fafc' } }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "Todos", value: -1 }]}
              colSpan={fields.length + 1}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { "aria-label": "filas por página" },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ '& .MuiTablePagination-toolbar': { fontSize: '0.78rem' }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.78rem' } }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
